import {
  ChannelBindingResponse,
  ChatSessionFileReferenceResponse,
  ChatSessionMessageResponse,
  ChatSessionResponse,
  ChatSessionThoughtTraceSummaryResponse,
  ChatSessionUxCapabilitiesResponse,
  ChatUploadDescriptorRequest,
  CreateWorkRequestRequest,
  PresentationEnvelopeContract,
  ProjectResponse,
  RespondToChatSessionInteractionRequest,
} from '@automatick/shared-ng';
import type {
  ConversationRuntimeMode,
  ConversationPulseKind,
  ConversationPulseSource,
  RealtimeAudienceScope,
  RealtimeRenderDisplayKind,
  RealtimeRenderDisplayStageLabel,
  RealtimeRenderEventStage,
  RealtimeRenderEventType,
  RealtimeRenderSeverity,
  RealtimeRenderState,
} from '@automatick/shared-ts';
import type { ConversationCompactionSystemNoticePayload } from '@automatick/shared-client-ts';
import { RealtimeConnectionHealth } from '../shared/realtime/realtime-connection-health';

export const WEBCHAT_PLATFORM_CODE = 'WebChat';
export const WEBCHAT_CONVERSATION_ID = 'web-control-app';
export const DEFAULT_STREAM_MODE = 'Delta';
export const NO_STREAM_MODE = 'None';
export const MESSAGE_HISTORY_TAKE = 80;
export const THOUGHT_TRACE_HISTORY_TAKE = 80;
export const TRANSCRIPT_DELTA_TAKE = 5;
export const MAX_RENDER_EVENT_HISTORY = 12;
export const MAX_PRESENTATION_RENDER_EVENT_HISTORY = 12;
export const TRANSCRIPT_REFRESH_DELAY_MS = 400;
export const TRANSCRIPT_REFRESH_MIN_INTERVAL_MS = 2000;
export const WEB_CHAT_TRANSCRIPT_REFRESH_RETRY_DELAY_MS = 700;
export const WEB_CHAT_TRANSCRIPT_REFRESH_MAX_RETRIES = 2;
export const TRANSCRIPT_VISIBLE_EVENT_STATES: ReadonlySet<RealtimeRenderState> =
  new Set([
    'completed',
    'failed',
    'waiting',
    'needs-input',
    'queued',
    'draft',
    'scheduled',
    'canceled',
    'cancelled',
    'dispatching',
  ]);

export const WebChatLocalRenderEventType = {
  RealtimeConnectionStatus: 'local.realtime.connection-status.v1',
} as const;

export type WebChatLocalRenderEventType =
  (typeof WebChatLocalRenderEventType)[keyof typeof WebChatLocalRenderEventType];

export const WebChatLocalRenderEventStage = {
  RealtimeConnectionStatus: 'local-realtime-connection-status',
} as const;

export type WebChatLocalRenderEventStage =
  (typeof WebChatLocalRenderEventStage)[keyof typeof WebChatLocalRenderEventStage];

export interface WebChatRenderEvent {
  streamId: string;
  audienceScope: RealtimeAudienceScope;
  eventType: RealtimeRenderEventType | WebChatLocalRenderEventType;
  sequence: number;
  checkpoint: string;
  workspaceId: string;
  requestedByUserId: string | null;
  sessionId: string | null;
  threadId: string | null;
  workRequestId: string | null;
  correlationId: string | null;
  sourceSurface: string;
  stage: RealtimeRenderEventStage | WebChatLocalRenderEventStage;
  state: RealtimeRenderState;
  message: string;
  displayKind: RealtimeRenderDisplayKind;
  displayStageLabel: RealtimeRenderDisplayStageLabel;
  promoteToFinalMessage: boolean;
  severity: RealtimeRenderSeverity;
  occurredOn: string;
  data: unknown;
  pulse: WebChatPulseMetadata;
  presentations: PresentationEnvelopeContract[];
  compactionNotice: ConversationCompactionSystemNoticePayload | null;
}

export interface WebChatCanonicalRenderEvent extends WebChatRenderEvent {
  eventType: RealtimeRenderEventType;
  stage: RealtimeRenderEventStage;
}

export interface WebChatLocalRealtimeStatusRenderEvent extends WebChatRenderEvent {
  eventType: typeof WebChatLocalRenderEventType.RealtimeConnectionStatus;
  stage: typeof WebChatLocalRenderEventStage.RealtimeConnectionStatus;
  compactionNotice: null;
}

export interface WebChatPulseMetadata {
  pulseKind: ConversationPulseKind | null;
  pulseSource: ConversationPulseSource | null;
  messageKey: string | null;
  locale: string | null;
  variantKey: string | null;
  isFallback: boolean;
  isDynamic: boolean;
  silenceBudgetSeconds: number | null;
  elapsedSinceLastMeaningfulPulseMs: number | null;
  operationKey: string | null;
  instructionId: string | null;
  correlationId: string | null;
}

export interface ThoughtTraceDataSnapshotField {
  name?: string | null;
  value?: ThoughtTraceDataSnapshot | null;
}

export interface ThoughtTraceDataSnapshot {
  valueKind?: string | null;
  textValue?: string | null;
  numberValue?: number | null;
  booleanValue?: boolean | null;
  fields?: ThoughtTraceDataSnapshotField[] | null;
  items?: ThoughtTraceDataSnapshot[] | null;
}

export interface WebChatRequestErrorBody {
  userMessage?: string | null;
  message?: string | null;
}

export interface WebChatThoughtTraceSummary extends Omit<
  ChatSessionThoughtTraceSummaryResponse,
  | 'thoughtTraceId'
  | 'sessionId'
  | 'workspaceId'
  | 'requestedByUserId'
  | 'workRequestId'
  | 'correlationId'
  | 'streamId'
  | 'audienceScope'
  | 'latestEventType'
  | 'latestStage'
  | 'latestState'
  | 'latestMessage'
  | 'latestDisplayKind'
  | 'latestDisplayStageLabel'
  | 'latestPromoteToFinalMessage'
  | 'latestSeverity'
  | 'entryCount'
  | 'lastSequence'
  | 'startedOn'
  | 'lastOccurredOn'
  | 'completedOn'
  | 'isActive'
> {
  thoughtTraceId: string;
  sessionId: string;
  workspaceId: string;
  requestedByUserId: string | null;
  workRequestId: string | null;
  correlationId: string | null;
  streamId: string;
  audienceScope: RealtimeAudienceScope;
  latestEventType: RealtimeRenderEventType;
  latestStage: RealtimeRenderEventStage;
  latestState: RealtimeRenderState;
  latestMessage: string;
  latestDisplayKind: RealtimeRenderDisplayKind;
  latestDisplayStageLabel: RealtimeRenderDisplayStageLabel;
  latestPromoteToFinalMessage: boolean;
  latestSeverity: RealtimeRenderSeverity;
  entryCount: number;
  lastSequence: number;
  startedOn: string;
  lastOccurredOn: string;
  completedOn: string | null;
  isActive: boolean;
}

export interface SendWebChatMessageOptions {
  platformAgentId?: string;
  requestedRuntimeMode?: ConversationRuntimeMode | null;
  uploads?: ChatUploadDescriptorRequest[];
  workRequestTemplate?: CreateWorkRequestRequest;
}

export interface RespondToInteractionOptions extends Partial<
  Pick<
    RespondToChatSessionInteractionRequest,
    'approved' | 'selectedChoiceId' | 'comment'
  >
> {
  platformAgentId?: string;
}

export interface PendingOutboundMessage {
  content: string;
  platformAgentId?: string;
  uploads?: ChatUploadDescriptorRequest[];
}

export interface WebChatState {
  phase: 'idle' | 'bootstrapping' | 'ready' | 'error';
  binding: ChannelBindingResponse | null;
  session: ChatSessionResponse | null;
  sessions: ChatSessionResponse[];
  projects: ProjectResponse[];
  selectedProjectId: string | null;
  uxCapabilities: ChatSessionUxCapabilitiesResponse | null;
  messages: ChatSessionMessageResponse[];
  sessionFiles: ChatSessionFileReferenceResponse[];
  isSending: boolean;
  isStoppingResponse: boolean;
  isAwaitingResponse: boolean;
  activeThoughtText: string | null;
  isLoadingHistory: boolean;
  isLoadingFiles: boolean;
  isSearchingSessions: boolean;
  isSearchingMessages: boolean;
  sessionSearchTerm: string;
  messageSearchTerm: string;
  liveTransport: 'idle' | 'connecting' | 'connected' | 'disconnected';
  liveStatusMessage: string;
  realtimeHealth: RealtimeConnectionHealth;
  latestRenderEvent: WebChatRenderEvent | null;
  latestSearchRenderEvent: WebChatRenderEvent | null;
  latestHealthCheckRenderEvent: WebChatRenderEvent | null;
  recentRenderEvents: WebChatRenderEvent[];
  presentationRenderEvents: WebChatRenderEvent[];
  thoughtChain: WebChatRenderEvent[];
  thoughtTraces: WebChatThoughtTraceSummary[];
  thoughtTraceEntriesById: Record<string, WebChatCanonicalRenderEvent[]>;
  loadingThoughtTraceIds: string[];
  activeThoughtTraceId: string | null;
  finalRenderMessage: ChatSessionMessageResponse | null;
  isThoughtChainActive: boolean;
  errorMessage: string;
}

export function isCanonicalWebChatIdentifier(
  value: string | null | undefined,
): value is string {
  return (
    typeof value === 'string' && value.length > 0 && value === value.trim()
  );
}

export function retainUniqueWebChatResources<T>(
  values: readonly T[],
  selectIdentifier: (value: T) => string | null | undefined,
): T[] {
  const counts = new Map<string, number>();
  for (const value of values) {
    const identifier = selectIdentifier(value);
    if (isCanonicalWebChatIdentifier(identifier)) {
      counts.set(identifier, (counts.get(identifier) ?? 0) + 1);
    }
  }

  return values.filter((value) => {
    const identifier = selectIdentifier(value);
    return (
      isCanonicalWebChatIdentifier(identifier) && counts.get(identifier) === 1
    );
  });
}
